import area from "@turf/area";
import { polygon, Polygon } from "@turf/helpers";
import intersect from "@turf/intersect";
import hash from "object-hash";

export interface Geometry {
  type: string;
  coordinates: number[][][];
}

export interface Building {
  geometry: Geometry;
  surface_area_sqm: number;
  center_distance?: number;
  key?: string;
  coordinatesHash?: string;
  polygonType?: string;
  sourceGeometry?: Geometry | null;
  sourcePolygonType?: string;
  id?: string;
  customised?: boolean;
}

export interface BmaBuilding {
  id?: string;
  surfaceAreaSqM: number;
  geometry: {
    type: string;
    coordinates: number[][][] | string;
  };
  sourceGeometry?: {
    type?: string;
    coordinates: number[][][] | string;
  } | null;
  polygonType?: string;
  sourcePolygonType?: string;
}

export const closeCoordinates = (coordinates: number[][]): number[][] => {
  const closedCoordinates = [...coordinates];
  if (coordinates[0][0] !== coordinates[coordinates.length - 1][0]) {
    // If the polygon is not closed close it
    closedCoordinates.push(coordinates[0]);
  }
  return closedCoordinates;
};

export const isIntersectingWithPolygon = (
  building: Building,
  polygons: Polygon[],
  threshold: number = 0.5
): boolean => {
  // Convert the input polygon to a Turf.js object
  const coordinates = building.geometry.coordinates[0];
  const closedCoordinates = closeCoordinates(coordinates);
  const buildingPolygon = polygon([closedCoordinates]);

  // Iterate over the array of polygons
  for (const polygonItem of polygons) {
    // Calculate the intersection between the input polygon and the current array polygon
    const intersection = intersect(buildingPolygon, polygonItem);

    // Check if there is an intersection and if its area is greater than the threshold
    if (
      intersection &&
      area(intersection) / area(buildingPolygon) > threshold
    ) {
      return true; // Intersection area is greater than the threshold, return true
    }
  }

  // No intersection found with area greater than the threshold
  return false;
};

export const bmaBuildingToOsmBuilding = (building: BmaBuilding): Building => {
  // Transform the building from our shape
  // to match the shape of buildings from OSM
  const surface_area_sqm = building.surfaceAreaSqM;
  const center_distance = 0; // For backward compatibility
  const coordinates =
    typeof building.geometry.coordinates === "string"
      ? JSON.parse(building.geometry.coordinates)
      : building.geometry.coordinates;
  const sourceGeometryCoordinates = building.sourceGeometry
    ? typeof building.sourceGeometry.coordinates === "string"
      ? JSON.parse(building.sourceGeometry.coordinates)
      : building.sourceGeometry.coordinates
    : null;
  const geometry: Geometry = {
    type: building.geometry.type,
    coordinates,
  };
  const sourceGeometry: Geometry | null = building.sourceGeometry
    ? {
        type: building.sourceGeometry.type || "",
        coordinates: sourceGeometryCoordinates,
      }
    : null;
  const polygonType = building.polygonType;
  const sourcePolygonType = building.sourcePolygonType;
  const coordinatesHash = hash(JSON.stringify(geometry.coordinates));
  const key = building.id || coordinatesHash;
  return {
    center_distance,
    geometry,
    sourceGeometry,
    polygonType,
    sourcePolygonType,
    surface_area_sqm,
    key,
    coordinatesHash,
  };
};
